import React, { useEffect, useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import {
  Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography,
  useMediaQuery, useTheme,
} from '@mui/material';
import { ScienceOutlined as TestCaseIcon } from '@mui/icons-material';
import { FieldMatchMode, DocumentSide, KycOperationType } from '@/types';
import { ocrTestingService } from '../ocr-testing/ocrTestingService';
import { useOcrLabels } from '../ocr-testing/ocrLabels';
import type { UploadMode } from './PhotoUploadPage';

/* The processing responses are loosely typed; only the members named here are read. */
interface CreateTestCaseDialogProps {
  open: boolean;
  onClose: () => void;
  uploadMode: UploadMode;
  frontImage: string | null;
  backImage: string | null;
  rawData: any;
  mappedData: any;
  compareMode: boolean;
  compareData: { mapped: { data: any }; raw: { data: any } } | null;
}

const uploadModeToOperationType = (mode: UploadMode): KycOperationType => {
  switch (mode) {
    case 'single': return KycOperationType.KycSingleSide;
    case 'dual-combined': case 'dual-separate': return KycOperationType.KycDualSide;
    case 'midni': return KycOperationType.QrMidni;
    default: return KycOperationType.KycSingleSide;
  }
};

/**
 * Turns the document just processed into an OCR test case: the current images, the values read
 * as the expected fields, and the case marked as validated.
 */
export const CreateTestCaseDialog: React.FC<CreateTestCaseDialogProps> = ({
  open, onClose, uploadMode, frontImage, backImage, rawData, mappedData, compareMode, compareData,
}) => {
  const t = useTranslate();
  const notify = useNotify();
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const labels = useOcrLabels();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [creating, setCreating] = useState(false);

  const docType = rawData?.description || mappedData?.Description || rawData?.documentInfo?.documentType || '';
  const nationality = rawData?.documentInfo?.nationality || mappedData?.Nationality || '';

  useEffect(() => {
    if (!open) return;
    // Auto-generate name from document info
    setName([docType, nationality].filter(Boolean).join(' — ') || t('screens.ocrTesting.newCase'));
    setDescription('');
  }, [open]);

  const handleCreate = async () => {
    if (!name.trim() || !frontImage) return;
    setCreating(true);
    try {
      // 1. Create test case with images
      const formData = new FormData();
      formData.append('name', name.trim());
      if (description) formData.append('description', description);
      formData.append('operationType', String(uploadModeToOperationType(uploadMode)));
      if (docType) formData.append('documentTypeName', docType);
      if (nationality) formData.append('nationalityCode', nationality);

      // Backend expects images[] + sides[] (parallel arrays) — see OcrTestController.CreateTestCase
      const frontBlob = await fetch(`data:image/jpeg;base64,${frontImage}`).then(r => r.blob());
      formData.append('images', frontBlob, 'front.jpg');
      formData.append('sides', String(DocumentSide.Front));
      if (backImage) {
        const backBlob = await fetch(`data:image/jpeg;base64,${backImage}`).then(r => r.blob());
        formData.append('images', backBlob, 'back.jpg');
        formData.append('sides', String(DocumentSide.Back));
      }

      const { data: created } = await ocrTestingService.createCase(formData);

      // 2. Save extracted fields as expected fields
      const currentKV: Record<string, string> = compareMode
        ? (compareData?.raw?.data?.keyValues || {})
        : (rawData?.keyValues || {});
      const currentMapped: Record<string, string> = Object.fromEntries(
        Object.entries((compareMode ? compareData?.mapped?.data : mappedData) ?? {})
          .filter(([, v]) => typeof v === 'string' || typeof v === 'number')
          .map(([k, v]) => [k, String(v)])
      );

      // Prefer mapped data if available, otherwise use raw keyValues
      const source = Object.keys(currentMapped).length > 0 ? currentMapped : currentKV;
      const expectedFields = Object.entries(source)
        .filter(([, v]) => v != null && v !== '')
        .map(([fieldName, expectedValue]) => ({
          fieldName,
          expectedValue: String(expectedValue),
          matchMode: FieldMatchMode.Exact,
        }));

      if (expectedFields.length > 0) {
        await ocrTestingService.saveFields(created.id, expectedFields);
        // Auto-validate since fields come from a real OCR result
        await ocrTestingService.validateCase(created.id);
      }

      notify('screens.testPage.caseCreated', { type: 'success' });
      onClose();
    } catch {
      notify('screens.ocrTesting.caseCreateFailed', { type: 'error' });
    } finally {
      setCreating(false);
    }
  };

  const fieldCount = Object.keys(
    compareMode
      ? (compareData?.mapped?.data || compareData?.raw?.data?.keyValues || {})
      : (mappedData || rawData?.keyValues || {})
  ).length;
  const summary = [
    labels.operationType(uploadModeToOperationType(uploadMode)),
    docType,
    nationality,
    t('screens.testPage.fieldCount', { smart_count: fieldCount }),
  ].filter(Boolean).join(' · ');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isPhone}>
      <DialogTitle>{t('test.create_test_case')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">{t('screens.testPage.createCaseHint')}</Typography>
          <TextField
            required
            fullWidth
            label={t('screens.testPage.caseName')}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <TextField
            fullWidth
            multiline
            rows={2}
            label={t('screens.testPage.caseDescription')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <Typography variant="body2" color="text.secondary">{summary}</Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>{t('ra.action.cancel')}</Button>
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={creating || !name.trim()}
          startIcon={creating ? <CircularProgress size={16} color="inherit" /> : <TestCaseIcon />}
        >
          {creating ? t('screens.ocrTesting.creating') : t('screens.ocrTesting.createCase')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
